import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-delta-value',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './delta-value.component.html',
  styleUrl: './delta-value.component.css'
})
export class DeltaValueComponent {
  constructor(private translocoService: TranslocoService) { }

  @Input() calculationNumber: number | null = null;
  @Input() extractionNumber: number | null = null;
  @Input() pod: any;
  @Input() invoice: any

  titleText: string = '';

  get delta(): number {
    const calc = Math.round((this.calculationNumber ?? 0) * 100) / 100;
    const ext = Math.round((this.extractionNumber ?? 0) * 100) / 100;
    const delta = Math.round((ext - calc) * 100) / 100;
    return delta;
  }

  get formattedDelta(): string {
    const d = this.delta;
    if (d === 0) return '0.00';
    return (d > 0 ? '+ ' : '- ') + Math.abs(d).toFixed(2);
  }

  get deltaClass(): string {
    const calc = this.calculationNumber ?? 0;
    const d = this.delta;

    if (d === 0) {
      return 'text-success';
    }

    this.titleText = this.getInvoiceDeltaTooltip(this.calculationNumber, this.extractionNumber, this.pod, this.invoice)
    if (this.titleText != '') {
      return 'text-blue-700';
    }

    // difference 
    const threshold = calc !== 0 ? Math.abs(calc) * 0.05 : 0.05;
    if (Math.abs(d) > threshold) {
      return 'text-error font-medium'; // deviation > 5%
    }
    return 'text-warning'; // within 5%
  }

  get canCalculateDelta(): boolean {
    if (this.calculationNumber == null || this.extractionNumber == null) {
      return false;
    }
    return true;
  }

  getInvoiceDeltaTooltip(calculationNumber: number | null | undefined, extractionNumber: number | null | undefined, pod?: any, invoice?: any): string {
    if (!pod || !invoice) return '';

    const r2 = (n: number) => Math.round(n * 100) / 100;
    const approxEq = (a: number, b: number, eps = 0.02) => Math.abs(a - b) <= eps;

    const toDelta = (calc: number | null | undefined, ext: number | null | undefined) => {
      const c = r2(calc ?? 0);
      const e = r2(ext ?? 0);
      return r2(e - c);
    };

    // component deviation for which we are checking
    const overallDelta = toDelta(calculationNumber, extractionNumber);
    if (approxEq(overallDelta, 0)) return '';

    // calculate deltas for all relevant fields
    const tmsDelta = toDelta(pod?.calc_data?.tms_charges_calc, pod?.tms_charges);
    const taxesDelta = toDelta(pod?.calc_data?.total_taxes_calc, pod?.total_taxes);

    const parts = [
      { label: 'shared.tms', delta: tmsDelta },
      { label: 'invoice.labels.total_taxes', delta: taxesDelta },
    ];

    const EPS = 0.02;
    const candidates = parts.filter(p => Math.abs(p.delta) > EPS);

    type Pick = { labels: string[]; sum: number };
    let bestExact: Pick | null = null;
    let bestNear: Pick | null = null;
    let bestNearDiff = Infinity;

    const n = candidates.length;
    for (let mask = 1; mask < (1 << n); mask++) {
      let sum = 0;
      const labels: string[] = [];
      for (let i = 0; i < n; i++) {
        if (mask & (1 << i)) {
          sum = r2(sum + candidates[i].delta);
          labels.push(candidates[i].label);
        }
      }

      if (approxEq(sum, overallDelta, EPS)) {
        if (!bestExact || labels.length < bestExact.labels.length ||
          (labels.length === bestExact.labels.length && Math.abs(sum - overallDelta) < Math.abs(bestExact.sum - overallDelta))) {
          bestExact = { labels, sum };
        }
      } else {
        const diff = Math.abs(sum - overallDelta);
        if (diff < bestNearDiff) {
          bestNearDiff = diff;
          bestNear = { labels, sum };
        }
      }
    }

    const picked = bestExact ?? bestNear;

    if (!picked || picked.labels.length === 0) {
      return this.translocoService.translate('admin.deviation_caused_by_multiple_factors');
    }

    const labels = picked.labels;
    const pretty =
      labels.length === 1
        ? this.translocoService.translate(labels[0])
        : labels.length === 2
          ? `${this.translocoService.translate(labels[0])} ${this.translocoService.translate('shared.and')} ${this.translocoService.translate(labels[1])}`
          : `${labels.slice(0, -1).map(label => this.translocoService.translate(label)).join(', ')} ${this.translocoService.translate('shared.and')} ${this.translocoService.translate(labels[labels.length - 1])}`;

    return `${this.translocoService.translate('admin.deviation_caused_by')} ${pretty}`;
  }
}
